// src/api/axiosConfig.ts
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

/**
 * Configure axios with retry logic for network resilience
 * Handles ERR_NETWORK_CHANGED and other transient network errors
 */

interface RetryConfig {
  retries?: number;
  retryDelay?: number;
  retryCondition?: (error: AxiosError) => boolean;
}

const defaultRetryConfig: Required<RetryConfig> = {
  retries: 2,
  retryDelay: 1000,
  retryCondition: (error: AxiosError) => {
    // Retry on network errors
    const shouldRetry = 
      error.code === 'ERR_NETWORK' ||
      error.code === 'ECONNABORTED' ||
      error.code === 'ETIMEDOUT' ||
      error.message === 'Network Error';
    
    return shouldRetry;
  },
};

/**
 * Add retry logic to axios instance
 */
export function setupAxiosRetry(instance: AxiosInstance = axios, config: RetryConfig = {}) {
  const finalConfig = { ...defaultRetryConfig, ...config };

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retryCount?: number };
      
      if (!originalRequest || !finalConfig.retryCondition(error)) {
        return Promise.reject(error);
      }

      originalRequest._retryCount = originalRequest._retryCount || 0;

      if (originalRequest._retryCount >= finalConfig.retries) {
        // Max retries reached - suppress logs for network errors (not actionable)
        const isNetworkError = 
          error.code === 'ERR_NETWORK' ||
          error.code === 'ECONNABORTED' ||
          error.code === 'ETIMEDOUT' ||
          error.message === 'Network Error';
        
        if (!isNetworkError) {
          console.warn(
            `Request failed after ${finalConfig.retries} retries:`,
            originalRequest.url
          );
        }
        return Promise.reject(error);
      }

      originalRequest._retryCount += 1;

      // Wait before retry (exponential backoff)
      const delay = finalConfig.retryDelay * originalRequest._retryCount;
      await new Promise((resolve) => setTimeout(resolve, delay));

      // Retry the request
      return instance(originalRequest);
    }
  );

  return instance;
}

/**
 * Create a pre-configured axios instance with retry logic
 */
export function createAxiosWithRetry(baseURL?: string, config: RetryConfig = {}) {
  const instance = axios.create({ baseURL });
  return setupAxiosRetry(instance, config);
}

// Setup global axios with retry logic
setupAxiosRetry(axios);

export default axios;

